/**
 * Building blocks of the operator actions: a button that opens a dialog, a reason field (every
 * action is audited with it), a one-time secret display and the common reason-only action.
 */

import type { LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { WriteOptions } from '../../api';
import { Callout, Field } from '../../components/blocks';
import { NotifyCustomerChoice, NotifyCustomerField } from '../../components/NotifyCustomer';
import { useWrite, WriteError } from '../../components/Write';

export const selectClass =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30';

export function ReasonField({ value, onChange, label }: { value: string; onChange: (v: string) => void; label?: string }) {
  const { t } = useTranslation('operator');
  return (
    <>
      <Field label={label ?? t('common.reason')} hint={t('common.reasonHint')}>
        <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} maxLength={500} required />
      </Field>
      <NotifyCustomerField />
    </>
  );
}

/** Close / submit row of a dialog form. */
export function FormFooter({ busy, disabled, submit, onClose, danger }: { busy: boolean; disabled?: boolean; submit?: string; onClose: () => void; danger?: boolean }) {
  const { t } = useTranslation('operator');
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onClose}>
        {t('common.close')}
      </Button>
      {submit ? (
        <Button type="submit" variant={danger ? 'destructive' : 'default'} disabled={busy || disabled}>
          {submit}
        </Button>
      ) : null}
    </DialogFooter>
  );
}

export interface DialogBodyProps {
  /** Closes the dialog (and reloads the page when something changed). */
  close: () => void;
  /** Marks that something changed, so the page reloads when the dialog closes. */
  changed: () => void;
}

/**
 * A button opening a dialog. The page reloads only when the dialog closes after a change, so a
 * result shown once (a key, a set-up link) is never unmounted before it is read.
 *
 * `open` / `onOpenChange` make it controlled from outside (e.g. an item of a "Thao tác" menu);
 * `trigger={false}` then leaves out the button. `defaultOpen` opens it on mount.
 */
export function ActionDialog({
  label,
  icon: Icon,
  title,
  description,
  variant = 'outline',
  disabled,
  wide,
  onChanged,
  open: openProp,
  onOpenChange,
  defaultOpen = false,
  trigger = true,
  children,
}: {
  label: string;
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  variant?: 'default' | 'outline' | 'destructive' | 'ghost';
  disabled?: boolean;
  /** `xl`: a grid of prices (one row per operation). */
  wide?: boolean | 'xl';
  onChanged?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  trigger?: boolean;
  children: (p: DialogBodyProps) => ReactNode;
}) {
  const [innerOpen, setInnerOpen] = useState(defaultOpen);
  const open = openProp ?? innerOpen;
  const setOpen = (o: boolean) => {
    if (openProp === undefined) setInnerOpen(o);
    onOpenChange?.(o);
  };
  const [dirty, setDirty] = useState(false);
  const close = () => {
    setOpen(false);
    if (dirty) {
      setDirty(false);
      onChanged?.();
    }
  };
  return (
    <>
      {trigger ? (
        <Button type="button" size="sm" variant={variant} disabled={disabled} onClick={() => setOpen(true)}>
          {Icon ? <Icon className="size-4" aria-hidden="true" /> : null}
          {label}
        </Button>
      ) : null}
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent className={`max-h-[92vh] overflow-y-auto [&>*]:min-w-0 ${wide === 'xl' ? 'sm:max-w-4xl' : wide ? 'sm:max-w-2xl' : 'sm:max-w-lg'}`} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>
          {open ? <NotifyCustomerChoice>{children({ close, changed: () => setDirty(true) })}</NotifyCustomerChoice> : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

/** A secret the server returns exactly once. */
export function SecretOnce({ label, value, hint }: { label: string; value: string; hint?: string }) {
  const { t } = useTranslation('operator');
  const [copied, setCopied] = useState(false);
  return (
    <Field label={label} hint={hint}>
      <div className="flex gap-2">
        <Input readOnly value={value} className="font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            void navigator.clipboard?.writeText(value).then(
              () => setCopied(true),
              () => undefined,
            )
          }
        >
          {copied ? t('common.copied') : t('common.copy')}
        </Button>
      </div>
    </Field>
  );
}

/** An action that needs nothing but a reason: the most common audited operator action. */
export function ReasonAction<T>({
  label,
  icon,
  title,
  description,
  warning,
  submit,
  danger,
  variant,
  disabled,
  run,
  done,
  onChanged,
}: {
  label: string;
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  warning?: ReactNode;
  submit?: string;
  danger?: boolean;
  variant?: 'default' | 'outline' | 'destructive' | 'ghost';
  disabled?: boolean;
  run: (reason: string, opts: WriteOptions) => Promise<T>;
  done: (result: T) => ReactNode;
  onChanged?: () => void;
}) {
  return (
    <ActionDialog label={label} icon={icon} title={title} description={description} variant={variant ?? (danger ? 'destructive' : 'outline')} disabled={disabled} onChanged={onChanged}>
      {(p) => <ReasonForm {...p} warning={warning} submit={submit ?? label} danger={danger} run={run} done={done} />}
    </ActionDialog>
  );
}

export function ReasonForm<T>({
  close,
  changed,
  warning,
  submit,
  danger,
  run,
  done,
}: DialogBodyProps & { warning?: ReactNode; submit: string; danger?: boolean; run: (reason: string, opts: WriteOptions) => Promise<T>; done: (result: T) => ReactNode }) {
  const [reason, setReason] = useState('');
  const w = useWrite((r: string, opts) => run(r, opts));
  const finished = w.state.phase === 'done';
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run(reason.trim()).then((r) => r !== undefined && changed());
      }}
    >
      {warning ? <Callout tone={danger ? 'crit' : 'warn'}>{warning}</Callout> : null}
      {!finished ? <ReasonField value={reason} onChange={setReason} /> : null}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {w.state.phase === 'done' ? <Callout tone="ok">{done(w.state.result)}</Callout> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={finished ? undefined : submit} onClose={close} danger={danger} />
    </form>
  );
}
